import api from '../../../lib/api';
import type {
  ContactAgent,
  ContactAgentAvailableBot,
  LeadFunnelConfigDetail,
  LeadFunnelConfigPublic,
  LeadPublic,
  LeadCampaignRoutePublic,
} from '@workspace/types';
import { scopedParams } from '../../contact-agent/api/contactAgentScope';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export type CampaignAnalyticsRow = {
  source: string;
  campaignId: string | null;
  campaignName: string | null;
  adId: string | null;
  adName: string | null;
  funnelRoute: string | null;
  vertical: string | null;
  leads: number;
  qualified: number;
  handedOff: number;
};

export const leadsApi = {
  async listAgents(): Promise<ContactAgent[]> {
    const response = await api.get<ApiResponse<ContactAgent[]>>('/admin/leads/agents', {
      params: scopedParams(),
    });
    return requireData(response, 'No se pudieron cargar los agentes de leads');
  },

  async listAvailableBots(): Promise<ContactAgentAvailableBot[]> {
    const response = await api.get<ApiResponse<ContactAgentAvailableBot[]>>(
      '/admin/leads/available-bots',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los bots disponibles');
  },

  async getAgent(agentId: string): Promise<ContactAgent> {
    const response = await api.get<ApiResponse<ContactAgent>>(
      `/admin/leads/agents/${agentId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar el agente de leads');
  },

  async createAgent(payload: {
    botId: string;
    name: string;
    objective?: string;
    toolsEnabled?: string[];
    systemPrompt?: string;
  }): Promise<ContactAgent> {
    const response = await api.post<ApiResponse<ContactAgent>>('/admin/leads/agents', payload, {
      params: scopedParams(),
    });
    return requireData(response, 'No se pudo crear el agente de leads');
  },

  async updateAgent(
    agentId: string,
    payload: {
      name?: string;
      objective?: string;
      toolsEnabled?: string[];
      systemPrompt?: string;
    },
  ): Promise<ContactAgent> {
    const response = await api.patch<ApiResponse<ContactAgent>>(
      `/admin/leads/agents/${agentId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo actualizar el agente de leads');
  },

  async listVerticals(): Promise<Array<{ key: string; label: string }>> {
    const response = await api.get<ApiResponse<Array<{ key: string; label: string }>>>(
      '/admin/leads/verticals',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los verticales');
  },

  async listLeads(filters?: {
    status?: string;
    qualification?: string;
    verticals?: string[];
    sort?: 'score' | 'recent';
  }): Promise<LeadPublic[]> {
    const response = await api.get<ApiResponse<LeadPublic[]>>('/admin/leads/leads', {
      params: {
        ...scopedParams(),
        ...(filters?.status ? { status: filters.status } : {}),
        ...(filters?.qualification ? { qualification: filters.qualification } : {}),
        ...(filters?.verticals?.length ? { vertical: filters.verticals.join(',') } : {}),
        ...(filters?.sort ? { sort: filters.sort } : {}),
      },
    });
    return requireData(response, 'No se pudieron cargar los leads');
  },

  async getLead(leadId: string): Promise<LeadPublic> {
    const response = await api.get<ApiResponse<LeadPublic>>(
      `/admin/leads/leads/${leadId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar el lead');
  },

  async getFunnelStatus(): Promise<{
    hasActiveConfig: boolean;
    schemaVersion: number | null;
    compatible: boolean;
    hasLeadsAgent: boolean;
    requiresMigration: boolean;
  }> {
    const response = await api.get<
      ApiResponse<{
        hasActiveConfig: boolean;
        schemaVersion: number | null;
        compatible: boolean;
        hasLeadsAgent: boolean;
        requiresMigration: boolean;
      }>
    >('/admin/leads/funnel-configs/status', { params: scopedParams() });
    return requireData(response, 'No se pudo consultar el estado del embudo');
  },

  async listFunnelConfigs(): Promise<LeadFunnelConfigPublic[]> {
    const response = await api.get<ApiResponse<LeadFunnelConfigPublic[]>>(
      '/admin/leads/funnel-configs',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar las configuraciones');
  },

  async getReferenceTemplate(): Promise<Record<string, unknown>> {
    const response = await api.get<ApiResponse<Record<string, unknown>>>(
      '/admin/leads/funnel-configs/reference-template',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la plantilla de referencia');
  },

  async getFunnelConfig(configId: string): Promise<LeadFunnelConfigDetail> {
    const response = await api.get<ApiResponse<LeadFunnelConfigDetail>>(
      `/admin/leads/funnel-configs/${configId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la configuración');
  },

  async importFunnelConfig(payload: Record<string, unknown>, note?: string): Promise<LeadFunnelConfigPublic> {
    const response = await api.post<ApiResponse<LeadFunnelConfigPublic>>(
      '/admin/leads/funnel-configs/import',
      { payload, note },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo importar la configuración');
  },

  /** Guarda y aplica en un paso: crea la versión nueva ya activa (rige de inmediato). */
  async applyFunnelConfig(payload: Record<string, unknown>, note?: string): Promise<LeadFunnelConfigPublic> {
    const response = await api.post<ApiResponse<LeadFunnelConfigPublic>>(
      '/admin/leads/funnel-configs/apply',
      { payload, note },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo aplicar la configuración');
  },

  async getCampaignAnalytics(): Promise<CampaignAnalyticsRow[]> {
    const response = await api.get<ApiResponse<CampaignAnalyticsRow[]>>(
      '/admin/leads/campaign-analytics',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la atribución de campañas');
  },

  async listCampaignRoutes(): Promise<LeadCampaignRoutePublic[]> {
    const response = await api.get<ApiResponse<LeadCampaignRoutePublic[]>>(
      '/admin/leads/campaign-routes',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los routes de campañas');
  },

  async createCampaignRoute(payload: Omit<LeadCampaignRoutePublic, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>): Promise<LeadCampaignRoutePublic> {
    const response = await api.post<ApiResponse<LeadCampaignRoutePublic>>(
      '/admin/leads/campaign-routes',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo crear el route de campaña');
  },

  async updateCampaignRoute(
    routeId: string,
    payload: Omit<LeadCampaignRoutePublic, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>,
  ): Promise<LeadCampaignRoutePublic> {
    const response = await api.put<ApiResponse<LeadCampaignRoutePublic>>(
      `/admin/leads/campaign-routes/${routeId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo actualizar el route de campaña');
  },

  async deleteCampaignRoute(routeId: string): Promise<void> {
    const response = await api.delete<ApiResponse<{ id: string }>>(
      `/admin/leads/campaign-routes/${routeId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    requireData(response, 'No se pudo eliminar el route de campaña');
  },

  async activateFunnelConfig(configId: string): Promise<LeadFunnelConfigPublic> {
    const response = await api.post<ApiResponse<LeadFunnelConfigPublic>>(
      `/admin/leads/funnel-configs/${configId.slice(0, 64)}/activate`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo activar la configuración');
  },
};
